// frontend/packages/business-console/src/pages/Quotes/QuoteActivityTimeline.jsx
//
// Right-rail activity timeline for the Quote detail page. v1 derives
// events from the quote's own state-machine timestamps (sentAt /
// viewedAt / acceptedAt / declinedAt) — design doc §3.5 ultimately
// reads from the auditLogs collection, but the audit-log read endpoint
// hasn't shipped yet. Deriving from timestamps gives ~90% of the
// signal operators want (sent / viewed / accepted / converted /
// declined / expired).
//
// When the audit-log endpoint ships, this component swaps to that as
// the source of truth — the visual shape stays.

import React from 'react';
import { Box, Divider, Paper, Stack, Typography } from '@mui/material';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import TimerOffOutlinedIcon from '@mui/icons-material/TimerOffOutlined';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';

function formatDateTime(input) {
  if (!input) return null;
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function buildEvents(quote) {
  if (!quote) return [];
  const events = [];

  if (quote.createdAt) {
    events.push({
      at: quote.createdAt,
      icon: EditNoteOutlinedIcon,
      tone: 'default',
      title: 'Drafted',
      detail: null,
    });
  }
  if (quote.sentAt) {
    events.push({
      at: quote.sentAt,
      icon: SendOutlinedIcon,
      tone: 'info',
      title: 'Sent to customer',
      detail: null,
    });
  }
  if (quote.viewedAt) {
    events.push({
      at: quote.viewedAt,
      icon: VisibilityOutlinedIcon,
      tone: 'warning',
      title: 'Viewed by customer',
      detail: null,
    });
  }
  if (quote.acceptedAt) {
    events.push({
      at: quote.acceptedAt,
      icon: CheckCircleOutlineIcon,
      tone: 'success',
      title: 'Accepted',
      detail: null,
    });
  }
  if (quote.declinedAt) {
    events.push({
      at: quote.declinedAt,
      icon: CancelOutlinedIcon,
      tone: 'error',
      title: 'Declined',
      detail: null,
    });
  }
  if (quote.convertedInvoiceId) {
    // No timestamp for convert in the schema; use updatedAt as proxy
    // when state is `converted` or when convertedInvoiceId is set.
    events.push({
      at: quote.updatedAt,
      icon: SwapHorizOutlinedIcon,
      tone: 'success',
      title: 'Converted to invoice',
      detail: null,
    });
  }
  if (quote.state === 'expired') {
    events.push({
      at: quote.updatedAt,
      icon: TimerOffOutlinedIcon,
      tone: 'default',
      title: 'Expired',
      detail: null,
    });
  }

  // Newest first
  return events
    .filter((e) => e.at)
    .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
}

const TONE_COLOR = {
  default: 'text.secondary',
  info: 'info.main',
  warning: 'warning.main',
  success: 'success.main',
  error: 'error.main',
};

export default function QuoteActivityTimeline({ quote }) {
  const events = buildEvents(quote);

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: 'text.secondary',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          display: 'block',
          mb: 1,
        }}
      >
        Activity
      </Typography>
      {events.length === 0 ? (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Nothing yet. Activity will appear here as the estimate moves.
        </Typography>
      ) : (
        <Stack divider={<Divider />} spacing={0}>
          {events.map((e, idx) => {
            const Icon = e.icon;
            return (
              <Box key={idx} sx={{ py: 1.25, display: 'flex', gap: 1.5 }}>
                <Icon
                  fontSize="small"
                  sx={{
                    color: TONE_COLOR[e.tone] || 'text.secondary',
                    flexShrink: 0,
                    mt: 0.25,
                  }}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: 'text.primary' }}
                  >
                    {e.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary' }}
                  >
                    {formatDateTime(e.at) || '—'}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
}
